import { useId, useState } from 'react';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Box, Container } from '@mui/material';
import { Link } from 'react-router-dom';
import { KC_FAQS_PATH, getHomepageFaqs } from '../repository';
import { KC_COLORS } from '../theme';

export function HomepageFaqs() {
  const faqs = getHomepageFaqs(5);
  const [openIndex, setOpenIndex] = useState(null);
  const reactId = useId();

  if (!faqs.length) return null;

  const openFaq = openIndex !== null ? faqs[openIndex] : null;
  const panelId = `${reactId}-home-faq-panel`;

  return (
    <Box
      className="kc-homepage-faqs w-full py-10 md:py-12"
      style={{ backgroundColor: KC_COLORS.white }}
    >
      <Container maxWidth="lg">
        <div
          className="flex flex-row items-start justify-between gap-4 mb-6 md:mb-7"
          style={{ textAlign: 'left' }}
        >
          <div className="min-w-0 flex-1" style={{ textAlign: 'left' }}>
            <h2
              className="kc-section-title kc-type-h3 uppercase tracking-[0.08em] m-0"
              style={{ color: KC_COLORS.primaryNavy, textAlign: 'left' }}
            >
              Knowledge Center FAQ
            </h2>
            <p
              className="mt-2 mb-0 kc-type-body max-w-2xl"
              style={{ color: KC_COLORS.gray600, textAlign: 'left' }}
            >
              Direct answers to common questions about AI in property and
              casualty insurance.
            </p>
          </div>

          <Link
            to={KC_FAQS_PATH}
            className="kc-type-body font-semibold no-underline whitespace-nowrap shrink-0 mt-1"
            style={{ color: KC_COLORS.teal, textAlign: 'right' }}
          >
            View all FAQs →
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            const buttonId = `${reactId}-home-faq-btn-${index}`;

            return (
              <button
                key={faq.id || `${faq.question}-${index}`}
                type="button"
                id={buttonId}
                className={`kc-home-faq-card group text-left rounded-2xl border bg-white px-4 py-4 md:px-4 md:py-5 transition-all duration-300 ${
                  isOpen ? 'kc-home-faq-card--open' : ''
                }`}
                style={{
                  borderColor: isOpen ? KC_COLORS.teal : KC_COLORS.border,
                  boxShadow: isOpen
                    ? '0 10px 28px rgba(0,155,170,0.12)'
                    : '0 1px 0 rgba(10,29,58,0.03)',
                  cursor: 'pointer',
                  minHeight: 132,
                }}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() =>
                  setOpenIndex((current) => (current === index ? null : index))
                }
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <span
                    className="kc-type-caption font-semibold tracking-[0.04em]"
                    style={{
                      color: isOpen ? KC_COLORS.teal : '#9AA7B8',
                    }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0 transition-all duration-300"
                    style={{
                      backgroundColor: isOpen
                        ? KC_COLORS.teal
                        : KC_COLORS.tealLight,
                      color: isOpen ? KC_COLORS.white : KC_COLORS.teal,
                      transform: isOpen ? 'rotate(180deg)' : 'none',
                    }}
                    aria-hidden
                  >
                    <ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />
                  </span>
                </div>

                <span
                  className="block font-semibold"
                  style={{
                    color: isOpen ? KC_COLORS.teal : KC_COLORS.primaryNavy,
                    fontSize: '15px',
                    lineHeight: '22px',
                  }}
                >
                  {faq.question}
                </span>
              </button>
            );
          })}
        </div>

        <div
          id={panelId}
          role="region"
          aria-live="polite"
          className="kc-home-faq-answer-panel"
          style={{
            display: 'grid',
            gridTemplateRows: openFaq ? '1fr' : '0fr',
            transition: 'grid-template-rows 0.34s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <div className="overflow-hidden min-h-0">
            {openFaq ? (
              <div
                className="mt-4 rounded-2xl border px-5 py-4 md:px-6 md:py-5"
                style={{
                  borderColor: 'rgba(0,155,170,0.28)',
                  backgroundColor: KC_COLORS.tealLight,
                }}
              >
                <p
                  className="m-0"
                  style={{
                    color: KC_COLORS.navy700,
                    fontSize: '15.5px',
                    lineHeight: 1.7,
                  }}
                >
                  {openFaq.answer}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </Container>
    </Box>
  );
}
